import { Loader2, Search } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserAvatarWithName } from "@/components/common/UserAvatarWithName";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { STUDYGROUP_MEMBERSHIP_STATUS_MEMBER } from "@/constants";
import type { MemberListProps } from "@/features/chat/types";
import { useStudyGroupMemberships } from "@/hooks/useStudyGroupMemberships";
import type { StudyGroupMembershipResponse } from "@/lib/humhub-api/types";
import { useGlobalUserInfo } from "@/providers/AuthProvider";

const MEMBERS_PER_PAGE = 50;

export const MemberList = ({
  selectedGroupId,
  selectedRecipients,
  onMemberSelect,
}: MemberListProps) => {
  const { t } = useTranslation();
  const userInfo = useGlobalUserInfo();
  const [inputName, setInputName] = useState<string>("");
  const isSearchingRef = useRef<boolean>(false);

  const {
    data: studyGroupMembers,
    isLoading: isLoadingMembers,
    error: membersError,
    pagination,
    fetchPage,
  } = useStudyGroupMemberships(
    selectedGroupId,
    MEMBERS_PER_PAGE,
    1,
    undefined,
    STUDYGROUP_MEMBERSHIP_STATUS_MEMBER,
  );

  const hasMoreMembers = pagination ? pagination.pages > 1 : false;

  const handleSearch = useCallback(
    (searchTerm: string) => {
      if (!selectedGroupId || isSearchingRef.current) return;

      isSearchingRef.current = true;
      fetchPage(1, searchTerm).finally(() => {
        isSearchingRef.current = false;
      });
    },
    [selectedGroupId, fetchPage],
  );

  const loadMoreMembers = () => {
    if (hasMoreMembers && pagination && !isSearchingRef.current) {
      const nextPage = pagination.page + 1;
      isSearchingRef.current = true;
      fetchPage(nextPage, inputName).finally(() => {
        isSearchingRef.current = false;
      });
    }
  };

  const getAvailableMembers = (): StudyGroupMembershipResponse[] => {
    if (!studyGroupMembers || studyGroupMembers.length === 0) {
      return [];
    }
    return studyGroupMembers.filter(
      (member) => member.user.guid !== userInfo?.guid && !member.is_blocked_by_current_user,
    );
  };

  const filteredMembers = getAvailableMembers().filter((member) => {
    return !selectedRecipients.some((r) => r.guid === member.user.guid);
  });

  if (!selectedGroupId) {
    return null;
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="relative mb-2">
        <Search className="absolute top-3 left-3 h-4 w-4 text-gray-400" />
        <div className="flex">
          <Input
            placeholder={t("chat.searchRecipients")}
            value={inputName}
            onChange={(e) => setInputName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch(inputName);
              }
            }}
            className="w-full rounded-r-none pl-10"
          />
          <Button
            onClick={() => handleSearch(inputName)}
            variant="teal"
            disabled={isLoadingMembers}
          >
            {t("common.search")}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden rounded-md border bg-[#F0F2F2]">
        <div className="max-h-60 overflow-y-auto">
          {isLoadingMembers ? (
            <div className="py-6 text-center text-[#565A5B] text-sm">
              <Loader2 size={20} className="mx-auto mb-2 animate-spin" />
              {t("common.loading")}
            </div>
          ) : membersError ? (
            <div className="py-6 text-center text-[#565A5B] text-sm">{t("common.error")}</div>
          ) : getAvailableMembers().length === 0 ? (
            <div className="py-6 text-center text-[#565A5B] text-sm">{t("chat.noMembers")}</div>
          ) : filteredMembers.length === 0 ? (
            <div className="py-6 text-center text-[#565A5B] text-sm">
              {t("chat.allMembersSelected")}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredMembers.map((member) => (
                <Button
                  key={member.user.guid}
                  onClick={() => onMemberSelect(member.user)}
                  variant="ghost"
                  className="w-full justify-start"
                >
                  <UserAvatarWithName user={member.user} size="sm" />
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>

      {!isLoadingMembers && hasMoreMembers && (
        <div className="mt-2">
          <Button variant="teal" size="sm" onClick={loadMoreMembers}>
            {t("common.showMore")}
          </Button>
        </div>
      )}
    </div>
  );
};
